// Wave partitions as candidate ridden trains (review SURF-2 and SURF-9; all-sports engine design
// 6.3, work package WP-S1). Pure: no I/O, environment, clock or randomness. Imported by the surf
// and bodyboard leaf scorers (their revision 2 formula paths only), by the surf and bodyboarding
// revision 2 kernels and by their v2 safety modules. It imports nothing from the engines, because
// page readers import the leaf scorers.
//
// Revision 1 anchors the ridden swell on the provider's "primary" label: a secondary swell only
// adds energy in proportion to its alignment with the primary, and only the primary and the wind
// sea are ever compared (surf-scoring-v5.ts effectiveSwell and windSeaAlternative). At a
// west-facing beach a blocked south swell labelled primary therefore hides an aligned west
// groundswell (review SURF-2). Revision 2 treats every complete partition (height and period both
// known) as a candidate ridden train and scores the hour from the best one:
//
// - Exposure: the share of a partition's energy that reaches the break, cos(delta) of the angle
//   between its direction (the direction the waves come from) and the beach facing, and exactly 0
//   at 90 degrees or more (waves from behind the beach line never arrive). This is the
//   shore-normal energy flux; refraction keeps the breaking height near H sqrt(cos delta). With
//   no beach facing every partition counts fully, revision 1's neutral central estimate (its
//   direction factor is 1 there). A partition with no direction at a known facing takes the
//   direction-averaged exposure (the design 2.5 neutral of a directional modifier).
// - Alignment: revision 1's partition alignment, 1 within 30 degrees and a cosine taper to 0 at
//   120 degrees; the direction-averaged value when either direction is unknown.
// - Organisation: 0 at 6 s or less and 1 at 10 s or more. A short-period wind sea is chop and never
//   part of a ridden swell.
// - For a candidate c, every other partition j merges into the ridden train with the energy
//   weight exposure_j x alignment_cj x min(organisation_c, organisation_j) (energy H^2 T, as
//   revision 1 merges its secondary swell), and the rest of it interferes: weight exposure_j x
//   (1 - alignment_cj x min(organisation)) on its energy H^2. The leaf's own chop curve prices
//   the interference height sqrt(sum w H^2) at its energy-weighted period. With directional
//   interference off, a partition interferes whatever its direction (exposure 1 on that side).
//
// Safety modules read the same partitions as exposed wave power (exposedPowerHeightM, energy flux
// H^2 T at a 10 s reference) with the largest single exposed height (largestExposedHeightM) as a
// floor, and the period of the dominant exposed train (dominantExposedPeriodS).
//
// Properties, pinned by lib/conditions/__tests__/swell-partitions.test.ts and the leaf tests:
// adding an off-window partition (exposure 0) never lowers the hour's best score, because it
// neither merges nor interferes and as a candidate can only raise the maximum; and relabelling
// or reordering the partitions changes nothing, because the candidates are built from a canonical
// order and every weight is symmetric in the labels.

/** One wave train at the break: height (already scaled by the spot's shelter), period and direction. */
export type WavePartition = Readonly<{
  heightM: number;
  periodS: number;
  /** Direction the waves come from, degrees; null when the provider gave none. */
  directionDeg: number | null;
}>;

/** A candidate ridden train with the cross-sea interference that reaches the break while it is ridden. */
export type RiddenTrain = Readonly<{
  heightM: number;
  periodS: number;
  directionDeg: number | null;
  /** sqrt(sum of interfering energy weights x H^2); 0 without interference. */
  interferenceHeightM: number;
  /** Energy-weighted period of the interference; null without interference. */
  interferencePeriodS: number | null;
}>;

export type RiddenTrainOptions = Readonly<{
  beachFacingDeg: number | null;
  /** SURF-9: weight interference by how much of each partition reaches the break. */
  directionalInterference: boolean;
}>;

export const SWELL_PARTITION_PARAMS = Object.freeze({
  windowHalfWidthDeg: 90,
  alignedWithinDeg: 30,
  unalignedFromDeg: 120,
  disorganisedAtOrBelowS: 6,
  organisedAtOrAboveS: 10,
  directionAverageStepDeg: 15,
  powerReferencePeriodS: 10,
});

/** Smallest angle between two bearings, 0 to 180 degrees. */
export function angleDiffDeg(a: number, b: number): number {
  const d = Math.abs((((a - b) % 360) + 360) % 360);
  return d > 180 ? 360 - d : d;
}

/**
 * The mean of a direction-dependent factor over every direction, sampled every `stepDeg` degrees
 * from 0: the neutral value of a directional modifier (design 2.5). Numerically identical to
 * engines/core/missing-data.ts directionAveragedFactor, which this module cannot import.
 */
export function directionAverage(factor: (directionDeg: number) => number, stepDeg: number = SWELL_PARTITION_PARAMS.directionAverageStepDeg): number {
  if (!(stepDeg > 0) || 360 % stepDeg !== 0) throw new RangeError("stepDeg must divide 360");
  let sum = 0;
  let count = 0;
  for (let direction = 0; direction < 360; direction += stepDeg) {
    sum += factor(direction);
    count++;
  }
  return sum / count;
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** A partition that can be ridden or can interfere: finite positive height and period, finite or null direction. */
export function isCompletePartition(partition: WavePartition): boolean {
  return finite(partition.heightM) && partition.heightM > 0 && finite(partition.periodS) && partition.periodS > 0 &&
    (partition.directionDeg === null || finite(partition.directionDeg));
}

/** Exposure of a known direction at a known facing: cos(delta) inside the window, exactly 0 outside. */
function knownExposure(directionDeg: number, beachFacingDeg: number): number {
  const delta = angleDiffDeg(directionDeg, beachFacingDeg);
  return delta >= SWELL_PARTITION_PARAMS.windowHalfWidthDeg ? 0 : Math.cos((delta * Math.PI) / 180);
}

/** The exposure a partition with no direction takes at a known facing. */
export const DIRECTION_AVERAGED_EXPOSURE = directionAverage((direction) => knownExposure(direction, 0));

/**
 * The share of a partition's energy that reaches the break (see the header): 1 without a beach
 * facing, `unknownDirection` (by default the direction-averaged exposure) without a direction.
 */
export function windowExposure(
  directionDeg: number | null,
  beachFacingDeg: number | null,
  unknownDirection: number = DIRECTION_AVERAGED_EXPOSURE,
): number {
  if (beachFacingDeg === null || !finite(beachFacingDeg)) return 1;
  if (directionDeg === null || !finite(directionDeg)) return unknownDirection;
  return knownExposure(directionDeg, beachFacingDeg);
}

/** Revision 1's alignment of two partitions by their separation (surf-scoring-v5.ts effectiveSwell). */
export function partitionAlignment(separationDeg: number): number {
  const { alignedWithinDeg, unalignedFromDeg } = SWELL_PARTITION_PARAMS;
  if (separationDeg <= alignedWithinDeg) return 1;
  if (separationDeg >= unalignedFromDeg) return 0;
  return (1 + Math.cos(((separationDeg - alignedWithinDeg) / (unalignedFromDeg - alignedWithinDeg)) * Math.PI)) / 2;
}

/** The alignment two partitions take when either direction is unknown. */
export const DIRECTION_AVERAGED_ALIGNMENT = directionAverage((direction) => partitionAlignment(angleDiffDeg(direction, 0)));

function alignmentBetween(a: number | null, b: number | null): number {
  return a === null || b === null ? DIRECTION_AVERAGED_ALIGNMENT : partitionAlignment(angleDiffDeg(a, b));
}

/** How organised a train is: 0 at 6 s or less (chop), 1 at 10 s or more (swell), linear between. */
export function waveOrganisation(periodS: number): number {
  const { disorganisedAtOrBelowS: low, organisedAtOrAboveS: high } = SWELL_PARTITION_PARAMS;
  if (!(periodS > low)) return 0;
  if (periodS >= high) return 1;
  return (periodS - low) / (high - low);
}

/** Canonical order (height, period, direction descending; unknown direction last), so labels never matter. */
function canonicalOrder(a: WavePartition, b: WavePartition): number {
  if (a.heightM !== b.heightM) return b.heightM - a.heightM;
  if (a.periodS !== b.periodS) return b.periodS - a.periodS;
  if (a.directionDeg === b.directionDeg) return 0;
  if (a.directionDeg === null) return 1;
  if (b.directionDeg === null) return -1;
  return b.directionDeg - a.directionDeg;
}

/**
 * Every complete partition as a candidate ridden train, in canonical order, with the partitions
 * that merge into it and the interference that reaches the break (see the header). Incomplete
 * partitions are ignored. Empty when no partition is complete.
 */
export function riddenTrains(partitions: readonly WavePartition[], options: RiddenTrainOptions): RiddenTrain[] {
  const complete = partitions.filter(isCompletePartition).sort(canonicalOrder);
  const exposure = complete.map((partition) => windowExposure(partition.directionDeg, options.beachFacingDeg));
  const organisation = complete.map((partition) => waveOrganisation(partition.periodS));
  return complete.map((candidate, index) => {
    const ownEnergy = candidate.heightM ** 2 * candidate.periodS;
    let energy = ownEnergy;
    let energyPeriod = ownEnergy * candidate.periodS;
    let merged = false;
    let x = candidate.directionDeg === null ? 0 : ownEnergy * Math.cos((candidate.directionDeg * Math.PI) / 180);
    let y = candidate.directionDeg === null ? 0 : ownEnergy * Math.sin((candidate.directionDeg * Math.PI) / 180);
    let interference = 0;
    let interferencePeriod = 0;
    complete.forEach((other, otherIndex) => {
      if (otherIndex === index) return;
      const share = alignmentBetween(candidate.directionDeg, other.directionDeg) * Math.min(organisation[index], organisation[otherIndex]);
      const mergeWeight = exposure[otherIndex] * share;
      if (mergeWeight > 0) {
        const merge = mergeWeight * other.heightM ** 2 * other.periodS;
        energy += merge;
        energyPeriod += merge * other.periodS;
        merged = true;
        if (other.directionDeg !== null) {
          x += merge * Math.cos((other.directionDeg * Math.PI) / 180);
          y += merge * Math.sin((other.directionDeg * Math.PI) / 180);
        }
      }
      const interferenceWeight = (options.directionalInterference ? exposure[otherIndex] : 1) * (1 - share);
      if (interferenceWeight > 0) {
        const part = interferenceWeight * other.heightM ** 2;
        interference += part;
        interferencePeriod += part * other.periodS;
      }
    });
    const periodS = merged ? energyPeriod / energy : candidate.periodS;
    return {
      heightM: merged ? Math.sqrt(energy / periodS) : candidate.heightM,
      periodS,
      directionDeg: candidate.directionDeg === null || !merged
        ? candidate.directionDeg
        : ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360,
      interferenceHeightM: interference > 0 ? Math.sqrt(interference) : 0,
      interferencePeriodS: interference > 0 ? interferencePeriod / interference : null,
    };
  });
}

/**
 * The exposed wave power of an hour as an equivalent height at the reference period (10 s):
 * sqrt(sum exposure_j H_j^2 T_j / 10). Energy flux grows with H^2 T, so a long-period swell is
 * more powerful than its height suggests (review SURF-13). Safety modules pass
 * `unknownDirection` 1: a partition whose direction is unknown is assumed to reach the break, so
 * a missing direction never hides a hazard.
 */
export function exposedPowerHeightM(
  partitions: readonly WavePartition[],
  beachFacingDeg: number | null,
  unknownDirection = 1,
): number {
  let energy = 0;
  for (const partition of partitions) {
    if (!isCompletePartition(partition)) continue;
    energy += windowExposure(partition.directionDeg, beachFacingDeg, unknownDirection) * partition.heightM ** 2 * partition.periodS;
  }
  return Math.sqrt(energy / SWELL_PARTITION_PARAMS.powerReferencePeriodS);
}

/**
 * The largest exposed height of any single partition: max H_j sqrt(exposure_j), the breaking height
 * the energy-flux model gives (see the header), whatever the period. Safety modules use it as a
 * floor under the power height, so a short-period storm sea is never rated far below its height;
 * with `unknownDirection` 1 a partition whose direction is unknown counts fully. 0 when none is
 * complete.
 */
export function largestExposedHeightM(
  partitions: readonly WavePartition[],
  beachFacingDeg: number | null,
  unknownDirection = 1,
): number {
  let largest = 0;
  for (const partition of partitions) {
    if (!isCompletePartition(partition)) continue;
    const height = partition.heightM * Math.sqrt(windowExposure(partition.directionDeg, beachFacingDeg, unknownDirection));
    if (height > largest) largest = height;
  }
  return largest;
}

/** The period of the partition carrying the most exposed energy, or null when none is complete. */
export function dominantExposedPeriodS(
  partitions: readonly WavePartition[],
  beachFacingDeg: number | null,
  unknownDirection = 1,
): number | null {
  let best: { energy: number; periodS: number } | null = null;
  for (const partition of [...partitions].filter(isCompletePartition).sort(canonicalOrder)) {
    const energy = windowExposure(partition.directionDeg, beachFacingDeg, unknownDirection) * partition.heightM ** 2 * partition.periodS;
    if (energy > 0 && (best === null || energy > best.energy)) best = { energy, periodS: partition.periodS };
  }
  return best?.periodS ?? null;
}

/** The revision 1 marine reading fields a leaf scorer receives, for callers that give no partitions. */
export type LeafMarineReading = Readonly<{
  swellWaveHeightM: number;
  swellWavePeriodS: number;
  swellWaveDirectionDeg: number | null;
  windWaveHeightM: number;
  windWavePeriodS?: number | null;
  windWaveDirectionDeg?: number | null;
  secondarySwellWaveHeightM?: number | null;
  secondarySwellWavePeriodS?: number | null;
  secondarySwellWaveDirectionDeg?: number | null;
  tertiarySwellWaveHeightM?: number | null;
  tertiarySwellWavePeriodS?: number | null;
  tertiarySwellWaveDirectionDeg?: number | null;
}>;

function directionOrNull(value: number | null | undefined): number | null {
  return finite(value) ? value : null;
}

/**
 * Partitions from a leaf marine reading, every height scaled by one shelter factor (revision 1's
 * convention). Revision 2 kernels pass their own partitions instead, each sheltered by its own
 * direction; this is the fallback for other callers of a revision 2 formula.
 */
export function partitionsFromLeafReading(marine: LeafMarineReading, shelterFactor: number): WavePartition[] {
  const partition = (height: number | null | undefined, period: number | null | undefined, direction: number | null | undefined): WavePartition[] =>
    finite(height) && finite(period) ? [{ heightM: height * shelterFactor, periodS: period, directionDeg: directionOrNull(direction) }] : [];
  return [
    ...partition(marine.swellWaveHeightM, marine.swellWavePeriodS, marine.swellWaveDirectionDeg),
    ...partition(marine.secondarySwellWaveHeightM, marine.secondarySwellWavePeriodS, marine.secondarySwellWaveDirectionDeg),
    ...partition(marine.tertiarySwellWaveHeightM, marine.tertiarySwellWavePeriodS, marine.tertiarySwellWaveDirectionDeg),
    ...partition(marine.windWaveHeightM, marine.windWavePeriodS, marine.windWaveDirectionDeg),
  ].filter(isCompletePartition);
}
